import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useIntl } from "react-intl";
import { DataGrid } from "@material-ui/data-grid";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import { Add, Delete } from "@material-ui/icons";
import RemoveRoundedIcon from "@material-ui/icons/RemoveRounded";
import AddRoundedIcon from "@material-ui/icons/AddRounded";

import { DEFAULT_PAGE_SIZE } from "../../../../utils/constants";
import {
  changeDiseasesAction,
  changeDisplayedPatientBackgroundsAction,
  changePatientBackgroundsSelectedToRemoveAction,
  changePatientBackgroundsToRemoveAction,
  changeSelectedPatientBackgroundsAction,
} from "../../../../reducers/searchOmimState";
import AddBackgroundsDialogComponent from "../patientFamilyBackgroundComponent/components/addBackgroundsDialogComponent/AddBackgroundsDialogComponent";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  dataContainer: {},
  dataHeader: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  header: {
    display: "flex",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    color: theme.palette.secondary.main,
  },
  dataGridContainer: {
    height: 400,
    width: "100%",
  },
  actionsCell: {
    display: "flex",
    alignItems: "center",
  },
  tableButtons: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  primaryButton: theme.primaryPinkButton,
  secondaryButton: theme.secondaryPinkButton,
  firstCap: {
    textTransform: "lowercase !important",
    "&:first-letter": {
      textTransform: "capitalize !important",
    },
  },
  iconButton: theme.iconButton,
  iconButtonDisable: theme.iconButtonDisable,
  bubble: {
    width: "1.5rem",
    height: "1.5rem",
    borderRadius: "50%",
    color: " #fff",
    backgroundColor: "#FF3185",
    placeItems: "center",
    display: "grid",
    position: "absolute",
    right: "-8px",
    top: "-10px",
  },
  actionButton: {
    "& :hover": {
      color: theme.palette.selected.main,
    },
  },
}));

const PatientBackgroundsComponent = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const newPatient = !!JSON.parse(localStorage.getItem("genome-new-patient"));

  const { patientData, patientBackgrounds } = useSelector((state) => state.patient);
  const { loading, mobileVersion } = useSelector((state) => state.common);
  const {
    displayedPatientBackgrounds,
    selectedPatientBackgrounds,
    patientBackgroundsToRemove,
    patientBackgroundsSelectedToRemove,
  } = useSelector((state) => state.searchOmim);

  const [openModal, setOpenModal] = useState(false);

  const isBackgroundSelected = (id) => {
    return !!patientBackgroundsToRemove.find((background) => background === id);
  };

  const handleSelectBackground = (background) => {
    let array = [...patientBackgroundsToRemove];

    const isSelected = isBackgroundSelected(background);

    if (isSelected) {
      array = array.filter((s) => s !== background);
    } else {
      array.push(background);
    }

    dispatch(changePatientBackgroundsToRemoveAction(array));
  };

  useEffect(() => {
    dispatch(changeDisplayedPatientBackgroundsAction(patientBackgrounds));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientBackgrounds]);

  useEffect(() => {
    if (!patientBackgroundsToRemove?.length && !!patientBackgroundsSelectedToRemove?.length) {
      dispatch(changePatientBackgroundsSelectedToRemoveAction([]));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientBackgroundsToRemove]);

  const columns = [
    {
      field: "actions",
      headerName: " ",
      width: 0.1,
      renderCell: (params) => {
        const isBackgroundInPatient = !!patientBackgrounds.find((s) => s.id === params.row.id);
        const isBackgroundToDelete = !!patientBackgroundsSelectedToRemove.find(
          (s) => s?.toString() === params.row?.id?.toString(),
        );

        return (
          <div className={classes.actionsCell}>
            {isBackgroundInPatient && !isBackgroundToDelete ? (
              <>
                <Checkbox
                  disabled={!isBackgroundInPatient}
                  size="small"
                  checked={isBackgroundSelected(params.row.id)}
                  onChange={() => handleSelectBackground(params.row.id)}
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
              </>
            ) : null}
          </div>
        );
      },
    },
    {
      field: "title",
      headerName: intl.formatMessage({ id: "searchOmimComponent.diseases" }),
      flex: 1,
    },
    {
      field: "status",
      headerName: " ",
      flex: 0.2,
      renderCell: (params) => {
        const isBackgroundInPatient = !!patientBackgrounds.find((s) => s.id === params.row.id);
        const isBackgroundToDelete = !!patientBackgroundsSelectedToRemove.find(
          (s) => s?.toString() === params.row?.id?.toString(),
        );

        return (
          <div className={classes.actionsCell}>
            {isBackgroundToDelete ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={intl.formatMessage({ id: "status_to_be_deleted" })}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleRestoreBackground(params.row.id, false)}
                  >
                    <RemoveRoundedIcon color={"secondary"} />
                  </IconButton>
                </Tooltip>
              </div>
            ) : isBackgroundInPatient ? null : (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={intl.formatMessage({ id: "status_to_be_added" })}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleRestoreBackground(params.row.id, true)}
                  >
                    <AddRoundedIcon color={"secondary"} />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const handleBackgroundsModal = (deleteBackgrounds = true) => {
    if (openModal) {
      deleteBackgrounds && dispatch(changeSelectedPatientBackgroundsAction([]));
      dispatch(changeDiseasesAction([]));
    }

    setOpenModal((openModal) => !openModal);
  };

  const handleDeleteSelectedBackgrounds = () => {
    restoreBackgrounds();
    dispatch(changePatientBackgroundsSelectedToRemoveAction(patientBackgroundsToRemove));
  };

  const handleAddBackgrounds = () => {
    restoreBackgrounds();
    handleBackgroundsModal(false);
  };

  const restoreBackgrounds = () => {
    const newArray = patientBackgrounds.concat(selectedPatientBackgrounds);

    dispatch(changeDisplayedPatientBackgroundsAction(newArray));
  };

  const handleRestoreBackground = (id, add) => {
    if (add) {
      const array = displayedPatientBackgrounds.filter((b) => b.id !== id);
      dispatch(changeSelectedPatientBackgroundsAction(array));
      dispatch(changeDisplayedPatientBackgroundsAction(array));
    } else {
      const copy = [...patientBackgroundsSelectedToRemove];
      const filtered = copy.filter((b) => b !== id);
      dispatch(changePatientBackgroundsSelectedToRemoveAction(filtered));
      dispatch(changePatientBackgroundsToRemoveAction(filtered));
    }
  };

  return (
    <div className={classes.dataContainer}>
      <div className={classes.dataHeader}>
        <div className={classes.header}>
          <Typography variant={"h6"}>
            {intl.formatMessage({ id: "patient" })} {newPatient ? "" : patientData?.patient_id}
          </Typography>
          <Typography variant={"h6"} className={classes.headerSubtitle}>
            / {intl.formatMessage({ id: "patientBackgrounds" })}
          </Typography>
        </div>

        <div className={classes.tableButtons}>
          {mobileVersion ? (
            <div className={classes.iconButton}>
              <Add onClick={handleBackgroundsModal} />
            </div>
          ) : (
            <Button className={classes.secondaryButton} onClick={handleBackgroundsModal}>
              <Typography className={classes.firstCap}>{intl.formatMessage({ id: "add_backgrounds" })}</Typography>
            </Button>
          )}
          {mobileVersion ? (
            <div className={patientBackgroundsToRemove?.length ? classes.iconButton : classes.iconButtonDisable}>
              <Delete onClick={patientBackgroundsToRemove?.length && handleDeleteSelectedBackgrounds} />
              {!!patientBackgroundsToRemove?.length && (
                <div className={classes.bubble}>{patientBackgroundsToRemove?.length}</div>
              )}
            </div>
          ) : (
            <Button
              className={classes.primaryButton}
              onClick={handleDeleteSelectedBackgrounds}
              disabled={!patientBackgroundsToRemove?.length}
            >
              <Typography className={classes.firstCap}>{intl.formatMessage({ id: "delete_selection" })}</Typography>
            </Button>
          )}
        </div>
      </div>
      <div className={classes.dataGridContainer}>
        <DataGrid
          autoHeight={false}
          rows={displayedPatientBackgrounds}
          columns={columns}
          disableSelectionOnClick
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
          loading={loading}
        />
      </div>
      {openModal && (
        <AddBackgroundsDialogComponent
          openModal={openModal}
          handleBackgroundsModal={handleBackgroundsModal}
          handleAddBackgrounds={handleAddBackgrounds}
          hideInheritance
        />
      )}
    </div>
  );
};

export default PatientBackgroundsComponent;
